<template>
	<div class="user-management">
		<div class="container">
			<!-- Header -->
			<div class="page-header flex-center">
				<h1 class="page-title">Announcements</h1>
			</div>
			<div class="page-content">
				<!-- Table Menu -->
				<div class="table-list-menu">
					<div class="row">
						<div class="col-sm-6">
							<div class="table-actions flex-center" v-if="isAdmin">
								<button class="btn btn-primary btn-sm" @click="showCreateModal">
									<i class="material-icons">add</i><span>Add New</span>
								</button>
								<button class="btn btn-light btn-sm btn-icon-danger" :class="{ 'is-loading': isDeleting }"
									:disabled="selectedItems.length === 0" @click="isShowDeleteBulk = true">
									<i class="material-icons">delete</i>
									<span>Delete</span>
								</button>
								<!-- Selected Label -->
								<div class="btn btn-plain btn-sm disabled" v-if="selectedItems.length > 0">
									<span><b>{{ selectedItems.length }}</b> Selected Announcement</span><span
										v-if="selectedItems.length > 1">s</span>
								</div>
							</div>
						</div>
						<div class="col-sm-6">
							<div class="table-filters d-flex align-items-center justify-content-end ml-a">
								<!-- Date Filter -->
								<daterangepicker :presets="filterDatePresets" :start="since" :end="until"
									@selected="setFilterDate" ref="datePicker">
								</daterangepicker>
							</div>
						</div>
					</div>
				</div>
				<!-- Announcements List -->
				<div class="users-list">
					<table class="users-list-table table table-primary table-striped">
						<thead>
							<tr>
								<th width="5%">
									<label class="input-checkbox-container">
										<input type="checkbox" class="input-checkbox" v-model="selectAll" />
										<span></span>
									</label>
								</th>
								<th width="10%" class="th-sort text-left" :class="sortClass('type')"
									@click="changeSorting('type')">
									Type
								</th>
								<th width="10%" class="th-sort text-left" :class="sortClass('level')"
									@click="changeSorting('level')">
									Level
								</th>
								<th width="10%" class="th-sort text-left" :class="sortClass('title')"
									@click="changeSorting('title')">
									Title
								</th>
								<th width="10%" class="th-sort text-left" :class="sortClass('visibility')"
									@click="changeSorting('visibility')">
									Visibility
								</th>
								<th width="10%" class="th-sort text-left" :class="sortClass('description')"
									@click="changeSorting('description')">
									Description
								</th>
								<th width="10%" class="th-sort text-left" :class="sortClass('is_active')"
									@click="changeSorting('is_active')">
									Status
								</th>
								<th width="10%" class="th-sort text-left" :class="sortClass('created_at')"
									@click="changeSorting('created_at')">
									Created At
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="item in items" :key="item._id">
								<td class="align-middle">
									<label class="input-checkbox-container">
										<input type="checkbox" class="input-checkbox" :value="item._id"
											v-model="selectedItems" />
										<span></span>
									</label>
								</td>
								<td>
									<div>{{ item.type }}</div>
								</td>
								<td>
									<div>{{ item.level }}</div>
								</td>
								<td>
									<div>{{ item.title }}</div>
								</td>
								<td>
									<div>{{ item.visibility }}</div>
								</td>
								<td>
									<div>{{ item.description }}</div>
								</td>
								<td>
									<checkbox-toggle :value="item.is_active"
										:toggle-info="item.is_active ? 'Deactivate' : 'Activate'"
										@input="toggleStatus(item)" />
								</td>
								<td>
									<div class="user__date" :title="formatDateWithTime(item.created_at)">
										{{ formatDate(item.created_at) }}
									</div>
								</td>
							</tr>
						</tbody>
					</table>
					<div class="table-fetch-status" v-if="isFetching">
						<div class="is-loading large dark m-auto"></div>
					</div>
					<!-- Empty -->
					<div class="list-empty" v-if="items.length === 0 && isFetching === false">
						<i class="material-icons mr-2">error_outline</i>
						<span v-if="keyword === ''">There is no announcements yet.</span>
						<span v-else>No announcements was found</span>
					</div>
				</div>
			</div>
		</div>
		<!-- Modals -->
		<div class="modals">
			<!-- Bulk Delete Confirm -->
			<modal class="coupon-editor-modal" size="medium" v-if="isShowDeleteBulk" @close="isShowDeleteBulk = false">
				<div slot="header">
					<h3><i class="material-icons">delete</i>Delete Announcements</h3>
				</div>
				<div slot="body">
					Are you sure want to delete {{ selectedItems.length }} announcements?
				</div>
				<div slot="footer">
					<button type="button" class="btn btn-light" @click="isShowDeleteBulk = false">
						{{ $t('Close') }}
					</button>
					<button type="button" class="btn btn-danger" @click="bulkDelete">
						Delete
					</button>
				</div>
			</modal>

			<!-- Add New Announcement -->
			<modal class="announcement-create-modal" size="medium" v-if="isShowCreateItem"
				@close="isShowCreateItem = false">
				<div slot="header">
					<h3><i class="material-icons">add</i>Create Announcement</h3>
				</div>
				<div slot="body">
					<div class="container-fluid">
						<div class="row">
							<div class="col-md-12">
								<div class="form-group">
									<label><b>Level</b></label>
									<select class="form-control" v-model="level">
										<option :value="level.key" v-for="(level, index) in noticeLevels" :key="index">{{
											level.label }}</option>
									</select>
								</div>
								<div class="form-group">
									<label><b>Announcement Visibility</b></label>
									<select class="form-control" v-model="visibility">
										<option :value="visibility.key" v-for="(visibility, index) in announcementVisibility" :key="index">{{
											visibility.label }}</option>
									</select>
								</div>

								<div class="form-group">
									<label><b>Title</b></label>
									<input type="text" name="title" class="input form-control" placeholder="Title"
										autocomplete="off" v-model="title" />
								</div>

								<div class="form-group">
									<label><b>Description</b></label>
									<textarea type="text" class="input form-control" placeholder="Description"
										v-model="description">
									</textarea>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div slot="footer">
					<button type="button" class="btn btn-light" @click="isShowCreateItem = false">
						Cancel
					</button>
					<button type="button" class="btn btn-primary" :class="{ 'is-loading': isCreating }" @click="create">
						Submit
					</button>
				</div>
			</modal>
		</div>

		<!-- Notifications -->
		<notifications group="announcements" position="bottom right" />
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import Modal from '@/components/Modal.vue';
import Daterangepicker from '@/components/Daterangepicker.vue';
import CheckboxToggle from '@/components/CheckboxToggle.vue';
import {
	duplicateVar,
	getDateRange,
} from '@/lib/helper';
import announcementsApi from '@/api/announcements';

export default {
	name: 'AdminAnnouncements',
	components: {
		Modal,
		Daterangepicker,
		CheckboxToggle,
	},
	data() {
		return {
			items: [],
			selectedItems: [],
			selectedItem: null,
			keyword: '',
			limit: 20,
			sort_by: 'created_at',
			sort: 'desc',
			isAll: false,
			isFetching: false,
			isCreating: false,
			isDeleting: false,
			isShowDeleteBulk: false,
			isShowCreateItem: false,
			since: null,
			until: null,
			noticeLevels: [
				{ key: 'info', label: 'Info' },
				{ key: 'warning', label: 'Warning' },
				{ key: 'danger', label: 'Danger' },
			],
			announcementVisibility: [
				{ key: 'public', label: 'Public' },
				{ key: 'dropshipper', label: 'Seller' },
				{ key: 'supplier', label: 'Supplier' },
			],
			type: 'notice',
			title: '',
			description: '',
			level: 'info',
			visibility: 'public',
			picture: '',
			link: '',
			releaseDate: '',
		};
	},
	methods: {
		create() {
			this.isCreating = true;

			const title = this.title.trim();
			const description = this.description.trim();

			const releaseDate = dayjs().format('YYYY-MM-DD');

			const params = {
				type: this.type,
				level: this.level,
				visibility: this.visibility,
				title,
				description,
				is_active: 1,
				release_date: releaseDate,
			};

			const callback = (response) => {
				this.isCreating = false;
				const item = response.data;
				this.items.unshift(item);
				this.isShowCreateItem = false;

				this.$notify({
					group: 'announcements',
					title: 'Announcements',
					text: 'Announcement has created',
					type: 'success',
				});
			};

			const errorCallback = (e) => {
				this.isCreating = false;
				const message = e.response.message;

				this.$notify({
					group: 'announcements',
					title: 'Announcements',
					text: message,
					type: 'error',
				});
			};

			announcementsApi.create(params, callback, errorCallback);
		},
		resetItems() {
			this.items = [];
			this.selectedItems = [];
			this.isAll = false;
			this.page = 1;
		},
		getFetchParams() {
			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
				since: this.since,
				until: this.until,
				type: this.type,
			};

			return params;
		},
		fetchItems(reset = true) {
			this.isFetching = true;
			if (reset) {
				this.resetItems();
			}

			const params = this.getFetchParams();

			const callback = (response) => {
				this.isFetching = false;
				const items = response.data;
				if (items.length > 0) {
					this.page++;
					this.items = this.items.concat(items);
				} else {
					this.isAll = true;
				}
			};

			const errorCallback = () => {
				this.isFetching = false;
			};

			announcementsApi.getList(params, callback, errorCallback);
		},
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchItems(false);
				}
			}
		},
		bulkDelete() {
			this.isDeleting = true;
			const ids = this.selectedItems.slice();
			const callback = () => {
				this.isDeleting = false;
				for (let i = 0; i < this.selectedItems.length; i++) {
					const id = this.selectedItems[i];
					const index = this.items.findIndex(item => item._id === id);
					const isIndex = index !== -1;
					if (isIndex) {
						this.items.splice(index, 1);
					}
				}
				this.$set(this, 'selectedItems', []);

				this.isShowDeleteBulk = false;
				this.$notify({
					group: 'announcements',
					title: 'Announcements',
					text: 'Announcements deleted',
					type: 'success',
				});
			};
			const errorCallback = () => {
				this.isDeleting = false;
				this.isShowDeleteBulk = false;
			};
			announcementsApi.bulkDelete(ids, callback, errorCallback);
		},
		changeSorting(key) {
			const isSame = this.sort_by === key;
			this.sort_by = key;
			const sort = isSame && this.sort === 'asc' ? 'desc' : 'asc';
			this.sort = sort;
			// Re-fetch
			this.fetchItems(true);
		},
		sortClass(key) {
			const sameKey = this.sort_by === key;
			const className = sameKey ? `th-sort-${this.sort}` : '';
			return className;
		},
		formatDate(date) {
			return this.moment(date).format('DD-MM-YYYY');
		},
		formatDateWithTime(date) {
			return this.moment(date).format('DD-MM-YYYY HH:mm:ss');
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const dateFormat = 'YYYY-MM-DD';
			const since = dayjs(filterDate.start).format(dateFormat);
			const until = dayjs(filterDate.end).format(dateFormat);
			this.since = since;
			this.until = until;
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchItems(true);
		},
		initFilterDate() {
			const dateRange = getDateRange();
			const hasSinceQuery = this.sinceQuery && this.sinceQuery !== '';
			const hasUntilQuery = this.untilQuery && this.untilQuery !== '';
			const sinceDate = hasSinceQuery
				? dayjs(this.sinceQuery)
				: dayjs(dateRange.start);
			const untilDate = hasUntilQuery
				? dayjs(this.untilQuery)
				: dayjs(dateRange.end);
			const filterDate = {
				start: sinceDate,
				end: untilDate,
			};
			this.filterDate = filterDate;

			const dateFormat = 'YYYY-MM-DD';
			this.since = sinceDate.format(dateFormat);
			this.until = untilDate.format(dateFormat);
		},
		fetchData() {
			if (this.isAdmin) {
				this.fetchItems();
			}
		},
		fetchAnnouncements() {
			this.fetchData();
		},
		toggleStatus(item) {
			const isActive = !item.is_active;
			const isActiveValue = isActive ? 1 : 0;
			const params = {
				is_active: isActiveValue,
			};
			const id = item._id;
			const message = isActive ? 'Activated' : 'Deactivated';
			const callback = (response) => {
				console.log(response);

				// Update
				const newItem = item;
				newItem.is_active = isActive;
				this.update(newItem);

				this.$notify({
					group: 'announcements',
					type: 'success',
					title: 'Announcement',
					text: message,
				});
			};
			const errorCallback = () => {
				this.$notify({
					group: 'announcements',
					type: 'warn',
					title: 'Announcement',
					text: 'Failed to update status',
				});
			};
			announcementsApi.update(id, params, callback, errorCallback);
		},
		update(item) {
			const index = this.items.findIndex(i => i._id === item._id);
			this.$set(this.items, index, item);
		},
		resetForm() {
			this.type = 'notice';
			this.title = '';
			this.description = '';
			this.level = 'info';
			this.visibility = 'public';
			this.picture = '';
			this.link = '';
			this.releaseDate = '';
		},
		showCreateModal() {
			this.resetForm();
			this.isShowCreateItem = true;
		},
	},
	computed: {
		...mapGetters({
			isAdmin: 'isAdmin',
			filterDatePresets: 'filterDatePresets',
		}),
		selectAll: {
			get() {
				return this.items
					? this.selectedItems.length === this.items.length
					: false;
			},
			set(value) {
				const selected = [];
				if (value) {
					this.items.forEach((item) => {
						selected.push(item._id);
					});
				}
				this.selectedItems = selected;
			},
		},
		sinceQuery() {
			const since = this.$route.query.since;
			return since;
		},
		untilQuery() {
			const until = this.$route.query.until;
			return until;
		},
	},
	watch: {
		isAdmin() {
			this.fetchAnnouncements();
		},
	},
	created() {
		this.initFilterDate();
		// Check Params & Fetch Data
		this.fetchAnnouncements();
		// On Scroll Bottom
		window.addEventListener('scroll', this.handleScroll);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>
